import React, { useState, useEffect } from 'react';
import { LangProvider, useLang } from './i18n/useLang';
import Nav from './components/Nav';
import Footer from './components/Footer';
import PartnersBanner from './components/PartnersBanner';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Blog from './pages/Blog';
import Learn from './pages/Learn';
import Offers from './pages/Offers';
import FAQ from './pages/FAQ';

const LANGS = ['en', 'fr'];

const pageSlugMap = {
  '': 'home',
  'home': 'home',
  'services': 'services',
  'about': 'about',
  'blog': 'blog',
  'learn': 'learn',
  'offers': 'offers',
  'faq': 'faq',
};

function parsePath(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  const hasLang = parts.length > 0 && LANGS.includes(parts[0]);
  const langSeg = hasLang ? parts[0] : 'en';
  const pageSeg = hasLang ? (parts[1] || '') : (parts[0] || '');
  return { lang: langSeg, page: pageSlugMap[pageSeg] || 'home' };
}

function buildPath(lang, page) {
  return page === 'home' ? `/${lang}` : `/${lang}/${page}`;
}

const pageTitles = {
  home: 'NovaFrik AI — Your Trusted AI Partner',
  services: 'Services — NovaFrik AI',
  about: 'About Us — NovaFrik AI',
  blog: 'Research & Blog — NovaFrik AI',
  learn: 'Learn — NovaFrik AI',
  offers: 'Offers — NovaFrik AI',
  faq: 'FAQ — NovaFrik AI',
};

function AppInner() {
  const { lang, setLang } = useLang();
  const [page, setPage] = useState(() => parsePath(window.location.pathname).page);
  const [theme, setTheme] = useState('cosmic');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const onPop = () => {
      const { lang: l, page: p } = parsePath(window.location.pathname);
      setLang(l);
      setPage(p);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [setLang]);

  useEffect(() => {
    document.title = pageTitles[page] || pageTitles.home;
  }, [page]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const navigate = (p) => {
    const path = buildPath(lang, p);
    window.history.pushState({ page: p, lang }, '', path);
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSetLang = (newLang) => {
    setLang(newLang);
    window.history.pushState({ page, lang: newLang }, '', buildPath(newLang, page));
  };

  const pages = { home: Home, services: Services, about: About, blog: Blog, learn: Learn, offers: Offers, faq: FAQ };
  const PageComponent = pages[page] || Home;

  return (
    <>
      <Nav currentPage={page} navigate={navigate} lang={lang} setLang={handleSetLang} theme={theme} setTheme={setTheme} />
      <main style={{ paddingTop: 80 }}>
        <PageComponent navigate={navigate} />
      </main>
      <PartnersBanner navigate={navigate} />
      <Footer navigate={navigate} />
    </>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AppInner />
    </LangProvider>
  );
}
